import React from 'react';
import { NextPageContext } from 'next';
import { TravellerWithMemories } from '../integrations/contentful/types';
import { fetchTravellerWithMemories } from '../integrations/contentful/queries';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { t } from '../utils/i18n';
import Container from '../components/grid/Container';
import Quote from '../components/common/Quote';
import SEO from '../components/head/SEO';
import Spacing from '../components/common/Spacing';
import H from '../components/elements/H';
import MemoryCard from '../components/memories/MemoryCard';
import BackButton from '../components/navigation/BackButton';
import { navbarLayout } from '../layouts/navbarLayout';
import TravellerInformation from '../components/travellers/TravellerInformation';
import HorizontalLine from '../components/common/HorizontalLine';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import { baseXInPixels, baseXUnit } from '../styles/theme';
import CmsMedia from '../components/cms/CmsMedia';
import Paragraph from '../components/elements/Paragraph';
import MediaGalleryModal from '../components/media-gallery/MediaGalleryModal';
import DisplayOn from '../components/common/DisplayOn';
import DivButton from '../components/a11y/DivButton';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { TimPage } from './TimPage';

interface TravellerPageProps {
  traveller?: TravellerWithMemories;
}

interface MediaGalleryState {
  show: boolean;
}

const ANALYTICS_CATEGORY = 'Matkaajasivu';

const TravellerPage: TimPage<TravellerPageProps> = ({ traveller }) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false
    });

  if (!traveller) {
    return null;
  }

  const travellerData = traveller.fields;
  const { profilePicture, title } = travellerData;
  const renderProfileImage = (imageSize: number) => {
    if (profilePicture) {
      return (
        <DivButton
          data-cy="traveller-page-main-media"
          onClick={() => {
            setMediaGalleryState({ show: true });
            trackAnalyticsEvent(
              ANALYTICS_CATEGORY,
              'Avattu mediagalleriamodaali',
              profilePicture.fields.title
            );
          }}
          data-testid="traveller-picture"
        >
          <CmsMedia
            media={profilePicture}
            width={imageSize}
            height={imageSize}
            borderRadius={1}
            crop
            hover
          />
        </DivButton>
      );
    }
  };
  return (
    <div data-cy="traveller-page">
      <SEO
        title={travellerData.seoTitle || title}
        canonicalPath={generatePath(Paths.Traveller, {
          slug: travellerData.slug
        })}
        description={
          travellerData.seoDescription || travellerData.leadParagraph
        }
        mainImage={profilePicture}
      />

      <Container>
        <Spacing top={13} bottom={[0, 2, 7]}>
          <Row>
            <Col size={[0, 7, 7]}>{renderProfileImage(baseXInPixels(50))}</Col>
            <Col size={[0, 1, 1]}></Col>
            <Col size={[18, 16, 16]}>
              <H styling="h5">{t('Matkaaja')}</H>
              <Spacing top={1} bottom={1}>
                <H styling="h1" data-testid="traveller-name">
                  <strong>{title}</strong>
                </H>
              </Spacing>
              <div className="short-info" data-testid="short-info">
                <Paragraph>{travellerData.shortInfo}</Paragraph>
              </div>
              <DisplayOn medium large>
                <Spacing top={6} bottom={6}>
                  <TravellerInformation
                    firstName={travellerData.firstName}
                    homepage={travellerData.homepage}
                    instagramHandle={travellerData.instagramHandle}
                    residence={travellerData.residence}
                  />
                </Spacing>
              </DisplayOn>
              <DisplayOn large>
                <Quote>{travellerData.leadParagraph}</Quote>
              </DisplayOn>
            </Col>
            <Col size={[6, 0, 0]}>
              <Spacing top={[3, 0, 0]}>
                {renderProfileImage(baseXInPixels(9))}
              </Spacing>
            </Col>
          </Row>
        </Spacing>
        <DisplayOn small>
          <Spacing top={6} bottom={6}>
            <TravellerInformation
              firstName={travellerData.firstName}
              homepage={travellerData.homepage}
              instagramHandle={travellerData.instagramHandle}
              residence={travellerData.residence}
            />
          </Spacing>
        </DisplayOn>
        <DisplayOn small medium>
          <div className="quote">
            <Quote>{travellerData.leadParagraph}</Quote>
          </div>
        </DisplayOn>
      </Container>
      <Spacing top={[5, 11, 11]} bottom={[7, 13, 13]}>
        <HorizontalLine expand={false} />
      </Spacing>
      <Container>
        <Spacing bottom={3}>
          <Row>
            <Col size={[24, 24, 7]}>
              <Spacing bottom={[4, 4, 0]}>
                <H styling={'h2'}>
                  <strong>
                    {t('Matkaajan')} {travellerData.firstName} {t('matkat')}
                  </strong>
                </H>
              </Spacing>
            </Col>
            <Col size={[0, 0, 1]} />
            <Col size={[24, 24, 16]}>
              <Row>
                {traveller.memories.map((cmsMemory, index) => {
                  const {
                    mainImage,
                    slug,
                    startDate,
                    endDate,
                    title,
                    subtitle,
                    matkanKesto,
                    golfInfo,
                    tripDepartures,
                    vehicleInfo
                  } = cmsMemory.fields;
                  return (
                    mainImage &&
                    (profilePicture || subtitle) && (
                      <Col size={[24, 12, 12]} key={cmsMemory.sys.id}>
                        <Spacing key={index} bottom={[4, 5, 5]}>
                          <MemoryCard
                            memory={{
                              mainImage,
                              slug,
                              startDate,
                              endDate,
                              title,
                              subtitle,
                              matkanKesto,
                              golfInfo,
                              tripDepartures,
                              vehicleInfo
                            }}
                            traveller={{
                              name: travellerData.title,
                              profilePicture,
                              slug: travellerData.slug
                            }}
                          />
                        </Spacing>
                      </Col>
                    )
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Spacing>
      </Container>
      {profilePicture && (
        <MediaGalleryModal
          items={[
            {
              media: profilePicture
            }
          ]}
          closeDialog={() =>
            setMediaGalleryState({ ...mediaGalleryState, show: false })
          }
          showDialog={mediaGalleryState.show}
        />
      )}

      {/*language=CSS*/}
      <style jsx>{`
        .short-info {
          max-width: ${baseXUnit(90)};
        }
        .quote {
          max-width: ${baseXUnit(65)};
        }
      `}</style>
    </div>
  );
};

TravellerPage.getLayout = (page) =>
  navbarLayout(
    <BackButton
      defaultTitle={t('Matkaajat')}
      defaultPath={Paths.TravellerList}
    />,
    page
  );

TravellerPage.getInitialProps = async function ({ query }: NextPageContext) {
  const travellerSlug = String(query.slug);
  const traveller = await fetchTravellerWithMemories(travellerSlug);
  const httpErrorCode = !traveller ? 404 : undefined;
  return { traveller, httpErrorCode };
};

export default TravellerPage;
